import {
  Button, SimpleForm,
  TextField, Toolbar, TopToolbar, useDataProvider, useNotify, useTranslate, useResourceContext, useRedirect,
  useListContext,
  WrapperField,
  Create,
} from 'react-admin';
import { Datagrid, IfCanAccess } from '@react-admin/ra-rbac';
import { ListLive } from '@react-admin/ra-realtime';
import {
  required, useCreatePath, useGetList, useRecordContext,
} from 'ra-core';
import { useNavigate } from 'react-router-dom';
import {
  Typography, TableCell,
  Chip,
  Stack,
  Box,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import { SecurityInventoryCategory, ExpiryTimeGranularityType, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import { LIST_DEBOUNCE } from '../../../config';
import SearchFields from '../components/fields/Filters';
import { TimeGranularityField } from '../../../components/fields/TimeGranularityField';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import { NotifiedPeopleFormValue, NotifiedPeopleInput } from '../components/inputs/NotifiedPeopleInput';
import { GrayExpiryTimeGranularityInputWithUpperLabel } from '../components/inputs/GrayExpiryTimeGranularityInputWithUpperLabel';
import { IconSelector } from '../components/inputs/IconSelector';
import { IconField } from '../components/fields/IconField';
import { InventoryCategoryIconName } from '../components/icons/inventoryCategoryIcons';
import { CustomReminderPeriodInput } from '../components/inputs/CustomReminderPeriodInput';
import { InventoryItemStatus, securityItemsResource } from './inventoryItems/util';
import { DEFAULT_REMINDER_PERIODS, getExpiryStatus } from '../../../utils/certificationsAndItemsExpiry';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { CreateFromPresetDialog, CategoryPresetKey } from '../components/CreateFromPresetDialog';
import { notifyWithError } from '../../../utils/errorHandling';
import { queryClient } from '../../../utils/queryClient';
import { StatusCounterCard } from '../components/StatusCounterCard';
import { getCurrentCustomer } from '../../../lib/currentCustomer';
import { RaXgacRecord } from '../../../lib/RaXgacRecord';
import {
  BhvkBackButton,
  BhvkSaveButton,
  BhvkTableDeleteButton,
  BhvkTableEditButton,
} from '../buttons/ActionButtons';

export type InventoryCategoryPresetKey =
  | 'fire_extinguishers'
  | 'fire_doors'
  | 'fire_dampers_ventilation'
  | 'fire_alarm_system'
  | 'fire_hose_reels'
  | 'emergency_lighting'
  | 'evacuation_alarm_system'
  | 'smoke_detectors'
  | 'sprinkler_system'
  | 'escape_route_signage'
  | 'aed_automated_external_defibrillator'
  | 'first_aid_kits'
  | 'co2_carbon_monoxide_detectors'
  | 'gas_detection_systems'
  | 'evacuation_plans'
  | 'ppe_personal_protective_equipment'
  | 'eyewash_station'
  | 'emergency_shower';

export type InventoryCategoryPreset = {
  presetKey: InventoryCategoryPresetKey;
  icon: InventoryCategoryIconName;
  expiryInterval: SecurityInventoryCategory['expiryInterval'];
};

type InventoryCategoryCounts = {
  valid: number;
  expiring: number;
  expired: number;
};

const PRESET_I18N_PREFIX = 'resources.security-inventory-items.presets';

const categoryPresets: InventoryCategoryPreset[] = [
  {
    presetKey: 'fire_extinguishers',
    icon: 'fire-extinguisher',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_doors',
    icon: 'meeting-room',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_dampers_ventilation',
    icon: 'air',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_alarm_system',
    icon: 'sensors',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'fire_hose_reels',
    icon: 'water-drop',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'emergency_lighting',
    icon: 'wb-iridescent',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'evacuation_alarm_system',
    icon: 'campaign',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'smoke_detectors',
    icon: 'smoke-free',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'sprinkler_system',
    icon: 'water',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'escape_route_signage',
    icon: 'exit-to-app',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'aed_automated_external_defibrillator',
    icon: 'monitor-heart',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'first_aid_kits',
    icon: 'medical-services',
    expiryInterval: { type: ExpiryTimeGranularityType.Month, value: 6 },
  },
  {
    presetKey: 'co2_carbon_monoxide_detectors',
    icon: 'sensors',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'gas_detection_systems',
    icon: 'sensors',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'evacuation_plans',
    icon: 'map',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'ppe_personal_protective_equipment',
    icon: 'health-and-safety',
    expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'eyewash_station',
    icon: 'visibility',
    expiryInterval: { type: ExpiryTimeGranularityType.Month, value: 6 },
  },
  {
    presetKey: 'emergency_shower',
    icon: 'shower',
    expiryInterval: { type: ExpiryTimeGranularityType.Month, value: 6 },
  },
];

const presetToCreatePayload = (
  preset: InventoryCategoryPreset,
  translate: (key: string) => string,
  notifiedPeople?: NotifiedPeopleFormValue,
): Omit<SecurityInventoryCategory, '_id' | 'customer' | 'share' | 'createdAt' | 'updatedAt'> => ({

  name: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.name`),
  description: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.description`),
  icon: preset.icon,
  expiryInterval: preset.expiryInterval,
  notifiedPeople: {
    peopleToNotify: (notifiedPeople?.peopleToNotify ?? []).map((personId) => ({
      _id: personId,
      _ref: 'Asset',
    })),
    emails: notifiedPeople?.emails ?? [],
  },
  reminderPeriods: DEFAULT_REMINDER_PERIODS,
});

const useRedirectToInventoryItemsMap = () => {

  const redirect = useRedirect();
  const createPath = useCreatePath();

  return (categoryId?: string) => {

    const listPath = createPath({ resource: securityItemsResource, type: 'list' });

    if (categoryId) {

      redirect(`${listPath}?createItem=${encodeURIComponent(categoryId)}`);
      return;

    }

    redirect(listPath);

  };

};

const InventoryCategoryCreateFromPresetsDialog = (props: { open: boolean; onClose: () => void; onAddCustom: () => void }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [existingCategoryNames, setExistingCategoryNames] = useState<string[]>([]);
  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const notify = useNotify();
  const redirectToMap = useRedirectToInventoryItemsMap();
  const translate = useTranslate();
  useEffect(() => {

    if (!props.open) return;
    xgacDataProvider.getList('security-inventory-categories', {
      pagination: { page: 1, perPage: -1 },
    }).then(({ data }) => {

      setExistingCategoryNames(data.map((row) => row.name));

    });

  }, [props.open]);

  const handleSubmit = async (selectedPresetKeys: CategoryPresetKey[], notifiedPeople?: NotifiedPeopleFormValue) => {

    setIsLoading(true);
    try {

      const selected = categoryPresets.filter((row) => selectedPresetKeys.includes(row.presetKey));

      if (selected.length === 0) return;

      const results = await Promise.all(
        selected.map((item) => dataProvider.create(
          resource || 'security-inventory-categories',
          { data: presetToCreatePayload(item, translate, notifiedPeople) },
        )),
      );

      await queryClient.invalidateQueries({
        queryKey: ['security-inventory-categories'],
      });

      notify(
        `${translate('resources.security-inventory-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`,
        { type: 'success' },
      );

      props.onClose();

      const createdIds = results.map((r) => String(r.data.id));
      redirectToMap(createdIds.length === 1 ? createdIds[0] : undefined);

    } catch (error) {

      notifyWithError(notify, error);

    } finally {

      setIsLoading(false);

    }

  };

  const existingNamesSet = useMemo(() => new Set(existingCategoryNames), [existingCategoryNames]);

  return (
    <CreateFromPresetDialog
      open={props.open}
      onClose={props.onClose}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
      presets={categoryPresets}
      existingNamesSet={existingNamesSet}
      presetPrefix={PRESET_I18N_PREFIX}
      onAddCustom={props.onAddCustom}
      i18n={{
        title: 'resources.security-inventory-categories.text.add_from_presets_title',
        searchPlaceholder: 'resources.security-inventory-categories.presets.text.search',
        noResults: 'resources.security-inventory-categories.text.no_results',
        presetAlreadyExists: 'resources.security-inventory-categories.text.preset_already_exists',
        peopleToNotifyTitle: 'resources.security-inventory-categories.text.people_to_notify_title',
        peopleToNotifyLabel: 'resources.security-inventory-categories.fields.people_to_notify',
        peopleToNotifyHelper: 'resources.security-inventory-categories.text.people_to_notify_helper',
      }}
      renderExtraHeadCells={() => (
        <>
          <TableCell>{translate('resources.security-inventory-categories.fields.icon')}</TableCell>
          <TableCell>
            {translate('resources.security-inventory-categories.fields.inspection_period')}
          </TableCell>
        </>
      )}
      renderExtraBodyCells={(row, textColor) => (
        <>
          <TableCell>
            {'icon' in row && <IconField source="icon" iconName={row.icon} />}
          </TableCell>
          <TableCell sx={{ color: textColor }}>
            {'expiryInterval' in row && translate(`general.text.time_granularity_types.${row.expiryInterval.type}`, { smart_count: row.expiryInterval.value })}
          </TableCell>

        </>
      )}
    />
  );

};

const inventoryCategoryTransformer = (data: any) => {

  return {
    ...data,
    notifiedPeople: {
      peopleToNotify: (data.notifiedPeople?.peopleToNotify ?? []).map((personId: string) => ({
        _id: personId,
        _ref: 'Asset',
      })),
      emails: data.notifiedPeople?.emails ?? [],
    },
  };

};

const InventoryCategoryFields = () => {

  return (
    <Stack direction="column" spacing={3} sx={{ width: '100%' }}>
      <GrayTextInputWithUpperLabel
        source="name"
        validate={required()}
        isRequired
        label="general.fields.name"
        helperText="resources.security-inventory-categories.text.name_helper"
        sx={{ width: '50%' }}
        FormHelperTextProps={{
          sx: {
            margin: 0,
            marginLeft: 0,
          },
        }}
      />
      <GrayTextInputWithUpperLabel
        source="description"
        label="general.fields.description"
        multiline rows={4}
        helperText={false}
        sx={{ width: '50%' }}
      />
      <IconSelector
        source="icon"
        label="resources.security-inventory-categories.fields.icon"
        validate={required()}
        isRequired
      />
      <NotifiedPeopleInput
        label="resources.security-inventory-categories.fields.people_to_notify"
        helperText="resources.security-inventory-categories.text.people_to_notify_helper"
        isRequired
      />
      <CustomReminderPeriodInput
        helperText="resources.security-inventory-categories.text.reminder_periods_helper"
        isRequired
      />
      <GrayExpiryTimeGranularityInputWithUpperLabel
        source="expiryInterval"
        label="resources.security-inventory-categories.fields.inspection_period"
        validate={required()}
        isRequired
        helperText="resources.security-inventory-categories.text.inspection_period_helper"
      />
    </Stack>
  );

};

const InventoryCategoryFormToolbar = () => {

  const navigate = useNavigate();

  return (
    <Toolbar sx={{ justifyContent: 'space-between', maxWidth: '1000px' }}>
      <BhvkBackButton onClick={() => navigate(-1)}/>
      <BhvkSaveButton />
    </Toolbar>
  );

};

export const InventoryCategoryCreate = () => {

  const translate = useTranslate();
  const notify = useNotify();
  const redirectToMap = useRedirectToInventoryItemsMap();
  const toastLabel = `${translate('resources.security-inventory-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`;

  return (
    <Create
      transform={inventoryCategoryTransformer}
      title="resources.security-inventory-categories.title"
      redirect={false}
      mutationOptions={{
        onSuccess: async (data) => {

          notify(toastLabel, { type: 'success' });
          await queryClient.invalidateQueries({
            queryKey: ['security-inventory-categories'],
          });
          redirectToMap(String(data.id));

        },
      }}
    >
      <SimpleForm
        defaultValues={{
          expiryInterval: { type: ExpiryTimeGranularityType.Year, value: 1 },
          reminderPeriods: DEFAULT_REMINDER_PERIODS,
        }}
        toolbar={ <InventoryCategoryFormToolbar/> }
        sx={{ maxWidth: '1000px' }}
      >
        <h4>{translate('resources.security-inventory-categories.text.create_title')}</h4>
        <Typography variant="body1">{translate('resources.security-inventory-categories.text.category_explanation')}</Typography>
        <hr/>
        <InventoryCategoryFields/>
      </SimpleForm>
    </Create>
  );

};

const InventoryCategoryDeleteButton = (props: { label: string; countsByCategoryId: Record<string, InventoryCategoryCounts> }) => {

  const translate = useTranslate();
  const record = useRecordContext();
  const counts = props.countsByCategoryId[record?._id ?? ''] ?? {
    valid: 0, expiring: 0, expired: 0,
  };
  const itemCount = counts.valid + counts.expiring + counts.expired;

  return (
    <BhvkTableDeleteButton
      mutationMode="pessimistic"
      confirmTitle={
        `${translate('ra.action.delete')} ${translate('resources.security-inventory-categories.text.title_single').toLowerCase()} "${record?.name}"`}
      confirmContent={translate(
        'resources.security-inventory-categories.text.delete_warning',
        { smart_count: itemCount },
      )}
      label={props.label}
    />
  );

};

const InventoryItemsCountField = (props: {
  label: string;
  countsByCategoryId: Record<string, InventoryCategoryCounts>;
}) => {

  const record = useRecordContext<SecurityInventoryCategory>();
  const redirect = useRedirect();
  const createPath = useCreatePath();
  const translate = useTranslate();

  const counts = props.countsByCategoryId[record?._id ?? ''] ?? {
    valid: 0, expiring: 0, expired: 0,
  };

  const handleMapRedirect = (recordId: string, status: InventoryItemStatus) => {

    return redirect(`${createPath({ resource: 'security-inventory-items', type: 'list' })}?category=${encodeURIComponent(recordId)}&status=${status}`);

  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        whiteSpace: 'nowrap',
        '& .MuiChip-root': {
          minWidth: 100,
        },
      }}
    >
      <Chip
        label={translate('resources.security-inventory-categories.text.valid_items_count', { count: counts.valid })}
        onClick={() => record && handleMapRedirect(record._id, 'valid')}
        disabled={counts.valid === 0}
      />
      <Chip
        label={translate('resources.security-inventory-categories.text.expiring_items_count', { count: counts.expiring })}
        onClick={() => record && handleMapRedirect(record._id, 'expiring')}
        disabled={counts.expiring === 0}
        sx={{ color: 'warning.main' }}
      />
      <Chip
        label={translate('resources.security-inventory-categories.text.expired_items_count', { count: counts.expired })}
        onClick={() => record && handleMapRedirect(record._id, 'expired')}
        disabled={counts.expired === 0}
        sx={{ color: 'error.main' }}
      />
    </Stack>
  );

};

const InventoryCategoriesDatagrid = () => {

  const { data: categories = [] } = useListContext<RaXgacRecord<SecurityInventoryCategory & { largestReminderPeriodInDays: number }>>();
  const translate = useTranslate();

  const { data: items = [] } = useGetList<RaXgacRecord<SecurityInventoryItem>>(securityItemsResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const countsByCategoryId = useMemo(() => {

    const reminderDaysByCategoryId = Object.fromEntries(
      categories.map((category) => [
        category._id,
        category.largestReminderPeriodInDays ?? 0,
      ]),
    );

    return items.reduce((counts: Record<string, InventoryCategoryCounts>, item) => {

      const categoryId = item.category._id;
      const current = counts[categoryId] ?? { valid: 0, expiring: 0, expired: 0 };

      const status = getExpiryStatus(item.expiryDate, reminderDaysByCategoryId[categoryId] ?? 0);
      if (status === 'valid') current.valid += 1;
      else if (status === 'expiring') current.expiring += 1;
      else if (status === 'expired') current.expired += 1;

      counts[categoryId] = current;
      return counts;

    }, {});

  }, [items, categories]);

  return (
    <Datagrid
      rowClick={false}
      bulkActionButtons={false}
      sx={{
        '& .column-actions': {
          width: '1%',
          whiteSpace: 'nowrap',
          paddingLeft: 1,
          paddingRight: 1,
        },
      }}
    >
      <TextField source="name" label="general.fields.name" />
      <InventoryItemsCountField
        countsByCategoryId={countsByCategoryId}
        label="resources.security-inventory-categories.fields.items_count"
      />
      <IconField source="icon" sortable={false} />
      <TimeGranularityField source="expiryInterval" label="resources.security-inventory-categories.fields.inspection_period"/>
      <AssetsInDialogButton
        label="resources.security-inventory-categories.fields.people_to_notify"
        resource="security-inventory-categories"
        nameColumnLabel={translate('resources.security-inventory-categories.fields.people_to_notify')}
        source="notifiedPeople.peopleToNotify"
        className="BhvkCompactContainedButton-root"
        closeClassName="BhvkCancelButton-root"
      />
      <WrapperField label="" sortable={false} source="actions">
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1}
          sx={{ whiteSpace: 'nowrap', marginLeft: 2, py: 1 }}
        >
          <BhvkTableEditButton
            resource="security-inventory-categories"
          />
          <InventoryCategoryDeleteButton label="ra.action.delete" countsByCategoryId={countsByCategoryId} />
        </Stack>
      </WrapperField>
    </Datagrid>
  );

};

export const InventoryCategoriesList = () => {

  const [createFromPresetsDialogOpen, setCreateFromPresetsDialogOpen] = useState(false);

  const translate = useTranslate();
  const resource = useResourceContext();
  const redirect = useRedirect();
  const currentCustomer = getCurrentCustomer()?.value;

  const { data: inventoryItemsCounts = { expiring: 0, expired: 0 } } = useQuery<{ expiring: number; expired: number }>({
    queryKey: ['security-inventory-items', 'expiry-counts', currentCustomer ?? ''],
    queryFn: async () => {

      if (!currentCustomer) return { expiring: 0, expired: 0 };

      const { json }: { json: { expiring: number; expired: number } } = await xgacDataProvider.getInventoryItemExpiryCounts(currentCustomer);
      return json;

    },
    enabled: Boolean(currentCustomer),
  });
  const handleAddCustom = () => {

    setCreateFromPresetsDialogOpen(false);
    redirect('create', resource);

  };

  return (
    <>
      <h3>{translate('resources.security-inventory-categories.title')}</h3>
      <p>{translate('resources.security-inventory-categories.text.sub_title')}</p>
      <Box display="flex" flexDirection="row" gap="0.625rem">
        <StatusCounterCard
          variant="expiring"
          count={inventoryItemsCounts.expiring}
          caption={translate('resources.security-inventory-items.text.number_of_expiring')}
          countLabel={translate('resources.security-inventory-items.text.item', { smart_count: inventoryItemsCounts.expiring })}
        />
        <StatusCounterCard
          variant="expired"
          count={inventoryItemsCounts.expired}
          caption={translate('resources.security-inventory-items.text.number_of_expired')}
          countLabel={translate('resources.security-inventory-items.text.item', { smart_count: inventoryItemsCounts.expired })}
        />
      </Box>
      <ListLive
        filters={SearchFields}
        debounce={LIST_DEBOUNCE}
        empty={false}
        actions={
          <IfCanAccess action="create">
            <TopToolbar>
              <Button label="general.text.add" variant="contained" size="medium" startIcon={<AddIcon/>} onClick={() => setCreateFromPresetsDialogOpen(true)} />
            </TopToolbar>
          </IfCanAccess>
        }
        sx={{ '& > .RaList-main > .RaList-content': { mt: '12px' } }}
      >
        <InventoryCategoriesDatagrid/>
      </ListLive>
      <Toolbar sx={{ justifyContent: 'flex-start', backgroundColor: 'white' }}>
        <BhvkBackButton onClick={() => redirect('list', 'security-inventory-items')}/>
      </Toolbar>
      <InventoryCategoryCreateFromPresetsDialog
        open={createFromPresetsDialogOpen}
        onClose={() => setCreateFromPresetsDialogOpen(false)}
        onAddCustom={handleAddCustom}
      />
    </>
  );

};

const InventoryCategoryEditForm = () => {

  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <>
      <h4 style={{ marginBottom: '10px' }}>"{record?.name}" {translate('general.text.settings')}</h4>
      <Typography variant="body1">{translate('resources.security-inventory-categories.text.settings_sub_title')}</Typography>
      <hr/>
      <InventoryCategoryFields/>
    </>
  );

};

export const InventoryCategoryEdit = () => {

  const translate = useTranslate();

  return (
    <>
      <EditWithSuccess
        transform={inventoryCategoryTransformer}
        title="resources.security-inventory-categories.title"
        actions={false}
        toastLabel={`${translate('resources.security-inventory-categories.text.title_single')} ${translate('general.text.updated').toLowerCase()}`}
      >
        <SimpleForm sx={{ maxWidth: '1000px' }} toolbar={<InventoryCategoryFormToolbar/>}>
          <InventoryCategoryEditForm/>
        </SimpleForm>
      </EditWithSuccess>
    </>
  );

};
